import React from 'react'
import {
  Truck,
  CheckCircle,
  Timer,
  Globe2,
  ShieldCheck,
  PackageSearch,
  BarChart3,
  PiggyBank,
  Route,
  MoveRight
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import deliveryImage from '../assets/images/data/ltl-delivery-process.png'
import MoreServicesSection from './MoreServicesSection'
// import FooterCTA from "./FooterCTA";
import CTA from './CTA'
// import DeliveryHighlights from "./DeliveryHighlights";

const LTLPage = () => (
  <main className='bg-white text-gray-800 overflow-hidden relative font-sans'>
    <Helmet>
      <title>Less Than Truckload (LTL) Shipping | S INFINITE EXPRESS</title>
      <meta
        name='description'
        content='Optimize your logistics with Less Than Truckload (LTL) shipping from S INFINITE EXPRESS – efficient, budget-friendly, and trackable freight services across India.'
      />
      <meta
        name='keywords'
        content='LTL shipping India, Less Than Truckload logistics, S INFINITE EXPRESS, Freight solutions, Affordable cargo transport, LTL tracking, partial truckload shipping, shared truck transport, LTL courier services, freight consolidation India, SME shipping solutions, intercity LTL delivery, logistics for small business'
      />
      <meta name='robots' content='index, follow' />
      <link rel='canonical' href='https://sinfiniteexpress.com/services/ltl' />
      <meta
        property='og:title'
        content='Less Than Truckload (LTL) Shipping | S INFINITE EXPRESS'
      />
      <meta
        property='og:description'
        content='Fast and affordable LTL shipping solutions in India by S INFINITE EXPRESS. Ideal for SMEs, trackable shipments, and nationwide reach.'
      />
      <meta
        property='og:url'
        content='https://sinfiniteexpress.com/services/ltl'
      />
      <meta property='og:type' content='website' />
    </Helmet>

    {/* INTRO */}
    <section className='max-w-7xl mx-auto py-20 px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center '>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className='space-y-6'
      >
        <h2 className='text-3xl md:text-4xl font-bold text-[#211f57]'>
          Why Choose Our LTL?
        </h2>
        <div className='w-20 h-1 bg-[#d32c30] rounded'></div>

        <p className='text-gray-700 text-lg'>
          <span className='font-bold text-[#d32c30]'>S INFINITE EXPRESS</span>{' '}
          offers reliable and efficient Less-Than-Truckload (LTL) services
          designed to meet diverse business needs across India. Our intelligent
          routing, centralized hubs, and real-time tracking ensure timely
          deliveries at optimized costs. With a strong focus on flexibility,
          visibility, and 24×7 customer support, we make freight movement
          smoother across every lane in India.
        </p>

        <motion.img
          src={deliveryImage}
          alt='LTL Delivery Process'
          className='rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700 border-4 border-white'
          loading='lazy'
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
      </motion.div>

      <div className='bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] text-black p-8 rounded-2xl shadow-xl space-y-6'>
        {/* Heading */}
        <h3 className='text-xl font-bold flex items-center gap-3'>
          <span className='w-10 h-10 rounded-lg bg-white/80 flex items-center justify-center shadow'>
            <Truck className='w-5 h-5 text-[#d32c30]' />
          </span>
          One Network. Every Destination.
        </h3>

        {/* Description */}
        <p className='text-black leading-relaxed'>
          LTL logistics made simple — fast, reliable, and cost-effective for
          growing businesses. Whether you ship occasionally or at scale, our
          adaptive network ensures
          <strong> predictable transit times</strong>,{' '}
          <strong>real-time visibility</strong>, and
          <strong> hassle-free documentation</strong>.
        </p>

        {/* Feature List */}
        <ul className='mt-10 grid sm:grid-cols-2 gap-4 text-sm text-gray-700'>
          {[
            'Centralized Pan-India hub-and-spoke network',
            'Mobile & web booking platform with instant confirmation',
            'Real-time tracking with proactive delay alerts',
            'Digital LR & ePOD available instantly',
            'Email & WhatsApp delivery notifications',
            'Flexible pickup schedules and door-to-door service',
            'Optimized for SMEs, distributors, and enterprise freight'
          ].map((item, i) => (
            <li key={i} className='flex items-start gap-3'>
              <span className='w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold'>
                ✓
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {/* Stats */}
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm'>
          <div className='bg-white/70 p-4 rounded-xl text-center shadow-sm'>
            <p className='text-2xl font-extrabold text-[#d32c30]'>99%</p>
            <p className='text-gray-800'>On-Time Delivery</p>
          </div>
          <div className='bg-white/70 p-4 rounded-xl text-center shadow-sm'>
            <p className='text-2xl font-extrabold text-[#d32c30]'>100%</p>
            <p className='text-gray-800'>ePOD Compliance</p>
          </div>
          <div className='bg-white/70 p-4 rounded-xl text-center shadow-sm'>
            <p className='text-2xl font-extrabold text-[#d32c30]'>24×7</p>
            <p className='text-gray-800'>Email & WhatsApp Updates</p>
          </div>
        </div>

        {/* CTA Button */}
        <a
          href='/get-a-quote'
          className='inline-flex items-center gap-2 px-6 py-3 bg-[#d32c30] text-white rounded-lg text-sm font-semibold hover:bg-[#b82629] transition shadow-md'
        >
          Start Shipping
          <MoveRight className='w-4 h-4' />
        </a>
      </div>
    </section>

    {/* Features */}
    <div className='py-20 bg-gradient-to-br from-[#211f57] to-[#2f2c72]'>
      <div className='max-w-7xl mx-auto px-4'>
        <h2 className='text-3xl md:text-4xl font-extrabold text-center text-[#fff44f] mb-4'>
          LTL Services: The Smart Choice for Growing Businesses
        </h2>
        <p className='text-center text-white max-w-3xl mx-auto mb-12'>
          Discover how our Less Than Truckload service combines cost efficiency,
          technology, and nationwide coverage to move your shipments better.
        </p>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
          {[
            {
              icon: <CheckCircle className='w-6 h-6 text-green-600' />,
              bg: 'bg-blue-50',
              title: 'Cost-Effective Shipping',
              description:
                'Pay only for the space you use — ideal for small to medium loads.'
            },
            {
              icon: <Timer className='w-6 h-6 text-blue-600' />,
              bg: 'bg-blue-100',
              title: 'Flexible Scheduling',
              description:
                'Choose pickup and delivery slots that suit your business flow.'
            },
            {
              icon: <Globe2 className='w-6 h-6 text-purple-600' />,
              bg: 'bg-blue-50',
              title: 'Pan India Network',
              description:
                'Delivering to over 18,000 pincodes with consistent reliability.'
            },
            {
              icon: <ShieldCheck className='w-6 h-6 text-orange-600' />,
              bg: 'bg-blue-100',
              title: 'Secure Handling',
              description: 'CCTV-monitored hubs and tamper-proof packaging.'
            },
            {
              icon: <PackageSearch className='w-6 h-6 text-red-600' />,
              bg: 'bg-blue-50',
              title: 'Live Tracking',
              description:
                'Track shipments in real-time with detailed milestone updates.'
            },
            {
              icon: <BarChart3 className='w-6 h-6 text-teal-600' />,
              bg: 'bg-blue-100',
              title: 'Performance Analytics',
              description:
                'Visualize delivery trends, peak zones, and turnaround time.'
            },
            {
              icon: <PiggyBank className='w-6 h-6 text-yellow-600' />,
              bg: 'bg-blue-50',
              title: 'Volume Discounts',
              description:
                'Enjoy lower rates as your shipment frequency increases.'
            },
            {
              icon: <Route className='w-6 h-6 text-pink-600' />,
              bg: 'bg-blue-100',
              title: 'Smart Routing',
              description:
                'AI-based route optimization to reduce fuel and time.'
            },
            {
              icon: <Truck className='w-6 h-6 text-[#211f57]' />,
              bg: 'bg-blue-50',
              title: 'Fleet of 300+ Trucks',
              description:
                'Reliable and ready to transport your cargo nationwide.'
            }
          ].map((feature, index) => (
            <div
              key={index}
              className={`group ${feature.bg} p-6 rounded-xl backdrop-blur-sm shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0]`}
            >
              <div className='flex items-center justify-center mb-4'>
                <div className='w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center transition-colors duration-300 group-hover:bg-white'>
                  <div className='transition-colors duration-300 group-hover:text-yellow-600'>
                    {feature.icon}
                  </div>
                </div>
              </div>

              <h3 className='text-lg font-bold text-[#211f57] group-hover:text-[#211f57] mb-2 group relative inline-block'>
                {feature.title}
                <span className='absolute left-0 -bottom-1 h-0.5 w-0 bg-[#211f57] transition-all duration-300 group-hover:w-full'></span>
              </h3>

              <p className='text-sm text-gray-700 group-hover:text-[#211f57] transition-colors duration-300'>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
    {/* <DeliveryHighlights /> */}
    <MoreServicesSection />
    <CTA />
  </main>
)

export default LTLPage
